import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import type { PaletteLike } from "@picnic-field/renderer/appearance";
import { isFeCode, toApiError } from "../../../lib/http/errors";
import type { ErrorCode } from "../../../lib/http/errors";
import { latestSnapshot, SnapshotRejected } from "../../../lib/api/fieldApi";
import type { Snapshot, Sources, SwapChange, UploadSelection } from "../../../lib/api/fieldApi";
import { useFieldApi } from "../../../hooks/useFieldApi";
import { useBenchHandoff } from "../../../hooks/useBenchHandoff";
import { clearPendingReturn, peekPendingReturn } from "../../../lib/bench/handoff";
import type { BenchReturn } from "../../../lib/bench/handoff";
import { usePicoToast } from "../../../components/PicoBubble";
import { feText } from "../../../i18n/fe";
import { useMyNickname } from "../../../hooks/useMyNickname";
import { Button, ButtonLink } from "../../../components/ui/Button";
import { FeBanner } from "../../../components/FeBanner";
import { PicoBubble } from "../../../components/PicoBubble";
import { ScreenRoot } from "../../../components/Shell";
import { useTopBar } from "../../../components/TopBar";
import { WorkCard } from "../../../components/WorkCard";
import { useDensity } from "../../../store/density";
import { useI18n } from "../../../i18n";
import { glbUrlFor, rememberGlbUrl } from "../../../lib/preview";
import { PicoReactionBubble, usePico } from "../../pico";
import { CheckPane } from "./CheckPane";
import type { CheckState } from "../types";
import { defaultProject, isFs02Code, visibleImports, visibleProjects } from "../lib/checks";
import { PickPane } from "./PickPane";
import { workTitle } from "../lib/titles";

/**
 * FS-02 내 작품 올리기(/upload) — ① 고르기(FS-02-01) → ② 확인하고 올리기(FS-02-02) → 올리기 완료(피코 반응 · 작품 카드).
 * 학생 자유 입력 0(DR-02) · 점수·순위 0(DR-01) · SE 는 큰 타일·아이콘, SU 는 목록·시각(DR-03).
 * FS-02-03 다시 올리기·되돌리기(P2-S6 · SR-RUN-05): 룸이 열려 있을 때 올리면 응답의 hotswap 으로 '룸에 바로 반영했어요'
 * (프로그램 · 조립 → 제자리에서 다시 나타남 · 색) + '↩ 되돌리기'(직전 버전 · 1회 — POST …/me/room/revert · 이미 했으면 409).
 */
type Phase =
  | { k: "pick" }
  | { k: "check"; sel: UploadSelection; state: CheckState; saving: boolean }
  | { k: "done"; snapshot: Snapshot; first: boolean };

/** 되돌리기 상태 — 1회만(성공·이미 함이면 버튼이 사라진다) */
type RevertState = "idle" | "busy" | "done" | "already" | "failed";

export function UploadScreen() {
  const { t, locale } = useI18n();
  const { density } = useDensity();
  const api = useFieldApi();
  const pico = usePico();

  const [sources, setSources] = useState<Sources | null>(null);
  const [palette, setPalette] = useState<PaletteLike | null>(null);
  const [loadError, setLoadError] = useState<ErrorCode | null>(null);
  const [loadSeq, setLoadSeq] = useState(0);
  const [importId, setImportId] = useState<string | null>(null);
  /** undefined = 아직 안 고름(기본 짝을 씀) · null = 전시만 */
  const [projectChoice, setProjectChoice] = useState<string | null | undefined>(undefined);
  const [refreshing, setRefreshing] = useState<string | null>(null);
  const [phase, setPhase] = useState<Phase>({ k: "pick" });
  const [revert, setRevert] = useState<RevertState>("idle");
  // 작품명 '…· 별명' 은 서버 별명(남에게 보이는 이름) — 세션 displayName(LTI 실명일 수 있음)은 쓰지 않는다
  const name = useMyNickname(phase.k) ?? "";

  // ── 목록 · 팔레트 불러오기 ─────────────────────────────────────
  useEffect(() => {
    if (!api) return;
    let cancelled = false;
    setLoadError(null);
    // 팔레트는 작품마다 제 appearance.palette 이름으로(2026-09-30 팔레트 버전 전환 — 옛 brick-v1 작품은 brick-v1 색)
    api
      .sources()
      .then(
        async (s) => [s, await api.palettes(s.campus.map((i) => i.appearance?.palette))] as const,
      )
      .then(([s, p]) => {
        if (cancelled) return;
        setSources(s);
        setPalette(p);
      })
      .catch((e) => !cancelled && setLoadError(toApiError(e).code));
    return () => {
      cancelled = true;
    };
  }, [api, loadSeq]);

  const imports = useMemo(() => visibleImports(sources?.campus ?? []), [sources]);
  const projects = useMemo(() => visibleProjects(sources?.bench ?? []), [sources]);
  const ready = imports.filter((i) => i.status === "ready");
  const imp = imports.find((i) => i.importId === importId && i.status === "ready") ?? null;
  // 한 개뿐이면 미리 고른다(SE 클릭 줄이기)
  const effectiveImport = imp ?? (ready.length === 1 ? (ready[0] ?? null) : null);
  const projectId =
    projectChoice === undefined ? defaultProject(effectiveImport, projects) : projectChoice;
  const project = projects.find((p) => p.projectId === projectId) ?? null;

  const refresh = useCallback(
    async (id: string) => {
      if (!api) return;
      setRefreshing(id);
      try {
        const next = await api.refreshImport(id);
        setSources((s) =>
          s ? { ...s, campus: s.campus.map((c) => (c.importId === id ? next : c)) } : s,
        );
        if (next.status === "ready") setImportId(next.importId);
      } catch (e) {
        setLoadError(toApiError(e).code);
      } finally {
        setRefreshing(null);
      }
    },
    [api],
  );

  // ── 검증 ─────────────────────────────────────────────────────
  const validate = useCallback(
    async (sel: UploadSelection) => {
      if (!api) return;
      setPhase({ k: "check", sel, state: { status: "checking" }, saving: false });
      let state: CheckState;
      try {
        const result = await api.validate(sel);
        state = { status: "result", result };
        const pv = result.preview;
        if (pv?.glbUrl) rememberGlbUrl(pv.modelSlug, pv.glbVersion, pv.glbUrl);
      } catch (e) {
        const err = toApiError(e);
        // 검증이 FS-02 코드로 거절되면(예: Bench 버전 없음 → FE-201) 결과처럼 보인다
        state = isFs02Code(err.code)
          ? {
              status: "result",
              result: {
                ok: false,
                issues: [{ rule: "", code: err.code, severity: "error" }],
                tags: [],
              },
            }
          : { status: "error", code: err.code };
      }
      setPhase((ph) => (ph.k === "check" && ph.sel === sel ? { ...ph, state } : ph));
    },
    [api],
  );

  // ── 'Bench 에서 고치기'(Sprint 4 · SR-INT-02) — 고른 프로젝트로 입장권 · 돌아오면 그 저장본으로 다시 확인 ──
  const toast = usePicoToast();
  const feToast = useCallback(
    (code: ErrorCode) => {
      const { cause, next } = feText(t, code, "student");
      toast.show(`upload-${code}`, [cause, next].filter(Boolean).join(" "));
    },
    [t, toast],
  );
  const [returnSeq, setReturnSeq] = useState(0);
  const handoff = useBenchHandoff(api, () => {
    toast.show("upload-bench-back", t("student.upload.benchBack"));
    setReturnSeq((n) => n + 1);
  });
  const fixInBench = (projectId: string) => {
    handoff.open(projectId).then(
      (ok) => {
        if (!ok) feToast("FE-201");
      },
      (e) => {
        const code = toApiError(e).code;
        feToast(isFeCode(code) ? code : "FE-201");
      },
    );
  };
  /** 고르기 중에 돌아온 저장본 — 그 프로젝트로 '다음'을 누르면 확인 · 확정까지 그 저장본으로 고정한다 */
  const [pin, setPin] = useState<BenchReturn | null>(null);
  /**
   * 돌아온 저장본으로 다시 확인 — 확인 중이면 그 가져오기 + 돌아온 프로젝트로 곧바로. 고르기 중이면 그 프로젝트를 고르고,
   * 가져오기가 정해지면(하나뿐 · 고름 · 그 프로젝트로 올린 내 최근 스냅샷의 모형) 곧바로 확인, 아니면 고정만 해 두고 '다음'을 기다린다.
   * 고정 해시(benchVersionHash)는 sel 에 남아 확정(createSnapshot)까지 간다. 어느 프로젝트인지 모르면 맡긴 채로 둔다.
   */
  const applyReturn = async (r: BenchReturn) => {
    if (!api) return;
    if (phase.k === "check") {
      const benchProjectId = r.projectId ?? phase.sel.benchProjectId;
      if (!benchProjectId) return;
      clearPendingReturn();
      setLoadSeq((n) => n + 1); // 고르기 목록의 저장본(versionHash · board)도 새로
      void validate({
        importId: phase.sel.importId,
        benchProjectId,
        benchVersionHash: r.benchVersionHash,
      });
      return;
    }
    const benchProjectId = r.projectId ?? projectId;
    if (!benchProjectId) return;
    clearPendingReturn();
    const pinned = { benchVersionHash: r.benchVersionHash, projectId: benchProjectId };
    setPin(pinned);
    setProjectChoice(benchProjectId);
    let imp = effectiveImport;
    if (!imp) {
      const mine = await api.mySnapshots().catch(() => []);
      const last = latestSnapshot(mine.filter((x) => x.programProjectId === benchProjectId));
      imp = last ? (ready.find((i) => i.modelSlug === last.modelSlug) ?? null) : null;
    }
    if (!imp) return;
    setImportId(imp.importId);
    void validate({
      importId: imp.importId,
      benchProjectId,
      benchVersionHash: r.benchVersionHash,
    });
  };
  const saving = phase.k === "check" && phase.saving;
  useEffect(() => {
    if (!api || !sources || phase.k === "done" || saving) return;
    const back = peekPendingReturn();
    if (back) void applyReturn(back);
    // applyReturn 은 매 렌더 새로 만들어진다 — 조건이 바뀔 때만 다시 본다
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, !!sources, phase.k, saving, returnSeq]);

  const checkRef = useRef<HTMLHeadingElement>(null);
  const next = () => {
    if (!effectiveImport) return;
    const pinned = pin && project?.projectId === pin.projectId ? pin.benchVersionHash : null;
    const sel: UploadSelection = project
      ? {
          importId: effectiveImport.importId,
          benchProjectId: project.projectId,
          ...(pinned ? { benchVersionHash: pinned } : {}),
        }
      : { importId: effectiveImport.importId };
    void validate(sel);
    // 확인 결과는 오른쪽 페인에 — 스크린 리더·키보드 시작점을 그쪽 제목으로 옮긴다
    checkRef.current?.focus();
  };

  const confirm = async () => {
    if (!api || phase.k !== "check" || phase.state.status !== "result") return;
    const { sel } = phase;
    const prev = phase.state;
    setPhase({ ...phase, saving: true });
    try {
      // 첫 올리기인가(피코 반응 first-upload) — 목록을 못 받으면 보통 올리기로
      const before = await api.mySnapshots().catch(() => null);
      const snapshot = await api.createSnapshot(sel);
      setRevert("idle");
      setPin(null);
      setPhase({ k: "done", snapshot, first: before?.length === 0 });
    } catch (e) {
      if (e instanceof SnapshotRejected) {
        const result = {
          ...prev.result,
          ok: false,
          issues: e.issues.length ? e.issues : prev.result.issues,
        };
        setPhase({
          k: "check",
          sel,
          saving: false,
          state: { status: "result", result, rejected: e.code },
        });
      } else {
        setPhase({
          k: "check",
          sel,
          saving: false,
          state: { status: "error", code: toApiError(e).code },
        });
      }
    }
  };

  // 올리기 완료 피코 반응(SR-ACH-03) — 계측은 한 번(말풍선은 화면 안에)
  const doneFirst = phase.k === "done" ? phase.first : null;
  const reactRef = useRef(pico.react);
  reactRef.current = pico.react;
  useEffect(() => {
    if (doneFirst == null) return;
    reactRef.current(doneFirst ? "first-upload" : "upload-done", { toast: false });
  }, [doneFirst]);

  const step = phase.k === "pick" ? "student.upload.stepPick" : "student.upload.stepCheck";
  useTopBar({
    icon: "⬆",
    title: t("student.upload.title"),
    chips: phase.k === "done" ? [] : [{ label: t(step), testId: "upload-step" }],
  });

  // ── 그리기 ───────────────────────────────────────────────────
  if (phase.k === "done") {
    const s = phase.snapshot;
    const reaction = pico.resolve(phase.first ? "first-upload" : "upload-done");
    return (
      <ScreenRoot
        id="FS-02"
        title={t("student.upload.doneTitle")}
        focusKey="done"
        className={`pf-pane pf-pane--hero pf-upload pf-upload--${density} pf-upload--done`}
      >
        <PicoReactionBubble reaction={reaction} />
        {s.hotswap && (
          <HotSwapNote
            applied={s.hotswap.applied}
            deferred={s.hotswap.deferred === true}
            change={s.hotswap.change}
            state={revert}
            onRevert={() => {
              if (!api || revert === "busy") return;
              setRevert("busy");
              api.revertRoom().then(
                (r) => setRevert(r),
                () => setRevert("failed"),
              );
            }}
          />
        )}
        <WorkCard
          work={{ ...s, title: workTitle(t, locale, s, name), glbUrl: glbUrlFor(s) }}
          palette={palette}
          live
          headingLevel={2}
          heading={t("student.home.myMachine")}
        />
        <div className="pf-upload__actions">
          <ButtonLink to="/home" variant="ghost" icon="🏠">
            {t("common.toEntrance")}
          </ButtonLink>
          <ButtonLink to="/gallery" variant="green" icon="🏛">
            {t("student.upload.toGallery")}
          </ButtonLink>
        </div>
      </ScreenRoot>
    );
  }

  let left: ReactNode;
  let right: ReactNode = null;
  if (loadError && !sources) {
    left = (
      <FeBanner
        code={loadError}
        audience="student"
        actions={[{ label: t("common.retry"), icon: "↻", onClick: () => setLoadSeq((n) => n + 1) }]}
      />
    );
  } else if (!sources) {
    left = <PicoBubble text={t("student.upload.loading")} busy live={false} />;
  } else {
    const checking = phase.k === "check";
    const selImp = checking
      ? (sources.campus.find((c) => c.importId === phase.sel.importId) ?? null)
      : null;
    const selPrj = checking
      ? (sources.bench.find((b) => b.projectId === phase.sel.benchProjectId) ?? null)
      : project;
    // 정적 안내의 '🧩 Bench 프로그램 고르기' — 달릴 수 있는 SPIKE(저장본 있음)가 있을 때만
    const runnable = defaultProject(selImp ?? effectiveImport, projects);
    // 확인 중에 왼쪽에서 다시 고르면 확인을 접고 고르기로 돌아간다(오른쪽 결과가 고른 것과 어긋나지 않게)
    const repick =
      <T,>(f: (v: T) => void) =>
      (v: T) => {
        if (phase.k === "check") setPhase({ k: "pick" });
        f(v);
      };
    left = (
      <>
        {loadError && <FeBanner code={loadError} audience="student" />}
        {!sources.benchAvailable && (
          <p className="pf-muted" data-testid="bench-down">
            {t("student.upload.benchDown")}
          </p>
        )}
        {imports.length === 0 && (
          <PicoBubble text={t("student.upload.pico.noImports")} live={false} />
        )}
        <PickPane
          imports={imports}
          projects={projects}
          palette={palette}
          importId={effectiveImport?.importId ?? null}
          projectId={projectId}
          refreshing={refreshing}
          onImport={repick(setImportId)}
          onProject={repick(setProjectChoice)}
          onRefresh={(id) => void refresh(id)}
          onNext={next}
        />
      </>
    );
    right = (
      <CheckPane
        imp={selImp}
        project={selPrj}
        state={phase.k === "check" ? phase.state : { status: "idle" }}
        palette={palette}
        displayName={name}
        saving={phase.k === "check" && phase.saving}
        onBack={() => setPhase({ k: "pick" })}
        onRetry={() => phase.k === "check" && void validate(phase.sel)}
        onConfirm={() => void confirm()}
        onFixInBench={handoff.available && selPrj ? () => fixInBench(selPrj.projectId) : null}
        onPickProgram={
          runnable
            ? () => {
                setPhase({ k: "pick" });
                setProjectChoice(runnable);
              }
            : null
        }
      />
    );
  }

  return (
    <ScreenRoot
      id="FS-02"
      title={t("student.upload.title")}
      hideTitle
      focusKey={phase.k === "pick" ? "pick" : undefined}
      className={`pf-upload pf-upload--${density}`}
    >
      <div className="pf-upload__panes" data-phase={phase.k}>
        <section
          className="pf-pane pf-pane--hero pf-upload__left"
          aria-label={t("student.upload.stepPick")}
        >
          {left}
        </section>
        <section
          className="pf-pane pf-pane--hero pf-upload__right"
          aria-labelledby="check-title"
          data-testid="fs-02-02"
        >
          <h2 id="check-title" className="pf-h3" tabIndex={-1} ref={checkRef}>
            {t("student.upload.checkTitle")}
          </h2>
          {right}
        </section>
      </div>
    </ScreenRoot>
  );
}

/**
 * FS-02-03 핫스왑 결과(룸이 열려 있을 때만) — 바꾼 종류별 안내 + 되돌리기 1회. 학생 문구는 피코 어휘(코드 없음 · 탓하지 않음).
 */
function HotSwapNote({
  applied,
  deferred,
  change,
  state,
  onRevert,
}: {
  applied: boolean;
  /** P3-S2 릴레이 — 내 구간이 도는 중이라 다음 시도에 바뀐다 */
  deferred: boolean;
  change: SwapChange | null;
  state: RevertState;
  onRevert: () => void;
}) {
  const { t } = useI18n();
  if (!applied)
    return (
      <p
        className="pf-banner pf-banner--info"
        role="status"
        data-testid="hotswap"
        data-applied="false"
      >
        {t(deferred ? "room.relay.swap.deferred" : "student.upload.swap.later")}
      </p>
    );
  const reverted = state === "done" || state === "already";
  return (
    <div
      className={`pf-banner ${reverted ? "pf-banner--info" : "pf-banner--ok"} pf-upload__swap`}
      role="status"
      data-testid="hotswap"
      data-applied="true"
      data-change={change ?? undefined}
      data-revert={state}
    >
      <p className="pf-banner__text">
        {reverted
          ? t(state === "done" ? "student.upload.swap.reverted" : "student.upload.swap.already")
          : t(`student.upload.swap.${change ?? "program"}`)}
        {state === "failed" && <> {t("student.upload.swap.revertFailed")}</>}
      </p>
      {!reverted && (
        <div className="pf-banner__actions">
          <Button
            variant="ghost"
            icon="↩"
            disabled={state === "busy"}
            onClick={onRevert}
            data-testid="hotswap-revert"
          >
            {t("student.upload.swap.revert")}
          </Button>
        </div>
      )}
    </div>
  );
}
